"use client";

import { Search, X } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { suggestPlaces } from "@/lib/locations";
import type { PlaceSuggestion } from "@/features/places/search";

type Props = {
  chosen: PlaceSuggestion | null;
  onChoose: (place: PlaceSuggestion | null) => void;
  placeholder?: string;
};

/**
 * A town or postcode search that only accepts its own suggestions: typing
 * offers stored places, and only a chosen one counts. Shared by the location
 * finder and the registration form's location step.
 */
export default function PlaceSearch({
  chosen,
  onChoose,
  placeholder = "Search by town, city or postcode",
}: Props) {
  const listId = useId();
  const [typed, setTyped] = useState(chosen?.label ?? "");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  // Ask once the typing pauses; a later answer replaces an earlier one only if it's still current.
  useEffect(() => {
    if (chosen && typed === chosen.label) return;
    let current = true;
    const timer = setTimeout(async () => {
      const found = await suggestPlaces(typed);
      if (!current) return;
      setSuggestions(found);
      setHighlighted(0);
    }, 200);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [typed, chosen]);

  const choose = (place: PlaceSuggestion) => {
    setTyped(place.label);
    setOpen(false);
    onChoose(place);
  };

  const clear = () => {
    setTyped("");
    setSuggestions([]);
    onChoose(null);
  };

  const showList = open && suggestions.length > 0 && typed !== chosen?.label;

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-das-gray"
        size={18}
        aria-hidden
      />
      <input
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        value={typed}
        onChange={(event) => {
          setTyped(event.target.value);
          setOpen(true);
          if (chosen) onChoose(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(event) => {
          if (!showList) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlighted((i) => Math.min(i + 1, suggestions.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlighted((i) => Math.max(i - 1, 0));
          } else if (event.key === "Enter") {
            event.preventDefault();
            choose(suggestions[highlighted]!);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
        className="h-12 w-full rounded-xl border border-[#86868b] bg-white pr-11 pl-11 text-[15px] text-das-black outline-none focus:border-blue-bright focus:ring-[3px] focus:ring-blue-bright/20"
      />
      {typed && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear the search"
          className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full p-1 text-das-gray hover:text-das-black"
        >
          <X size={16} aria-hidden />
        </button>
      )}

      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full right-0 left-0 z-20 mt-2 overflow-hidden rounded-xl border border-black/5 bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,.12)]"
        >
          {suggestions.map((place, index) => (
            <li
              key={place.id}
              role="option"
              aria-selected={index === highlighted}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(place);
              }}
              onMouseEnter={() => setHighlighted(index)}
              className={`cursor-pointer px-4 py-2.5 text-left text-[15px] text-das-black ${index === highlighted ? "bg-das-light" : ""}`}
            >
              {place.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
